import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

import { ApiPaths, api } from "@/lib/api";

import { BackupCodesPanel } from "./BackupCodesPanel";

type SetupResponse = { device_id: number; provisioning_uri: string };
type VerifyResponse = {
  detail: string;
  device_id: number;
  backup_codes: string[];
};

type Props = { onClose: () => void; onActivated: () => void };


function extractSecret(uri: string): string | null {
  try {
    const url = new URL(uri);
    return url.searchParams.get("secret");
  } catch {
    return null;
  }
}


export function MfaSetupModal({ onClose, onActivated }: Props) {
  const { t } = useTranslation();
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const secret = useMemo(() => (uri ? extractSecret(uri) : null), [uri]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.post<SetupResponse>(ApiPaths.authMfaSetup, {});
        if (!cancelled) setUri(res.data.provisioning_uri);
      } catch {
        if (!cancelled) setErr(t("ui.mfaSetupModal.errorSetupFailed"));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; t is stable
  }, []);

  async function verify() {
    setErr(null);
    setBusy(true);
    try {
      const res = await api.post<VerifyResponse>(ApiPaths.authMfaVerify, { code });
      // Show the recovery-codes screen instead of closing immediately.
      // onActivated fires after the user acknowledges saving them.
      setBackupCodes(res.data.backup_codes ?? []);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail ?? t("ui.mfaSetupModal.errorCodeRejected"));
    } finally {
      setBusy(false);
    }
  }

  async function copySecret() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard not available — user can long-press the field */
    }
  }

  if (backupCodes) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("ui.mfaSetupModal.recoveryDialogLabel")}
        className="modal-backdrop bg-black/70"
      >
        <div className="modal-panel w-full max-w-md">
          <BackupCodesPanel
            codes={backupCodes}
            onAcknowledged={onActivated}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.mfaSetupModal.dialogLabel")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-md space-y-4">
        <h2 className="text-lg font-semibold">{t("ui.mfaSetupModal.title")}</h2>
        <p className="text-sm text-nb-text-2">
          {t("ui.mfaSetupModal.intro")}
        </p>

        {uri ? (
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-md bg-white p-3">
              {/* react-qr-code renders pure SVG — no canvas, scales
                  crisply, and respects the dark/light contrast required
                  for authenticator-app scanners. */}
              <QRCode
                value={uri}
                size={192}
                viewBox="0 0 256 256"
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>
            <button
              type="button"
              className="text-xs text-nb-text-3 underline hover:text-nb-text-2"
              onClick={() => setShowManual((v) => !v)}
            >
              {showManual ? t("ui.mfaSetupModal.hideManual") : t("ui.mfaSetupModal.showManual")}
            </button>
            {showManual && secret && (
              <div className="w-full space-y-1">
                <label className="label" htmlFor="mfa-secret">
                  {t("ui.mfaSetupModal.secretKeyLabel")}
                </label>
                <div className="flex gap-2">
                  <input
                    id="mfa-secret"
                    className="input flex-1 font-mono text-xs"
                    value={secret}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={copySecret}
                  >
                    {copied ? t("ui.mfaSetupModal.copied") : t("ui.mfaSetupModal.copy")}
                  </button>
                </div>
                <p className="text-xs text-nb-text-3">
                  {t("ui.mfaSetupModal.totpParams")}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-nb-text-3">{t("ui.mfaSetupModal.generatingSecret")}</p>
        )}

        <div>
          <label className="label" htmlFor="mfa-code">{t("ui.mfaSetupModal.codeLabel")}</label>
          <input
            id="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="123456"
          />
        </div>
        {err && <p role="alert" className="text-sm text-cvp-danger">{err}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-ghost" onClick={onClose}>{t("ui.mfaSetupModal.cancel")}</button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || code.length < 6}
            onClick={verify}
          >
            {busy ? t("ui.mfaSetupModal.verifying") : t("ui.mfaSetupModal.activate")}
          </button>
        </div>
      </div>
    </div>
  );
}